import { supabase } from './supabaseClient';

/**
 * IndexedDB Service para armazenar arquivos binários (PDFs)
 * O localStorage tem limite de 5-10MB, PDFs precisam do IndexedDB.
 */

const DB_NAME = 'LibriTuiFiles';
const STORE_NAME = 'pdf_blobs';
const DB_VERSION = 1;

export const fileStorageService = {
  init: (): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  },

  clearOldestFiles: async (): Promise<void> => {
    const db = await fileStorageService.init();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAllKeys();

      request.onsuccess = () => {
        const keys = request.result;
        if (keys.length === 0) return resolve();

        // Remove 50% dos arquivos mais antigos (baseado na ordem das chaves)
        const toDelete = keys.slice(0, Math.ceil(keys.length / 2));
        console.warn(`[Storage] Quota excedida. Removendo ${toDelete.length} arquivos para liberar espaço.`);

        toDelete.forEach(key => store.delete(key));
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      };
      request.onerror = () => reject(request.error);
    });
  },

  saveFile: async (id: string, file: Blob): Promise<void> => {
    try {
      const db = await fileStorageService.init();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put(file, id);

        request.onsuccess = () => resolve();
        request.onerror = async () => {
          const error = request.error;
          if (error?.name === 'QuotaExceededError' || error?.message?.includes('space')) {
            console.error("[Storage] Erro de espaço detectado no saveFile. Iniciando limpeza...");
            try {
              await fileStorageService.clearOldestFiles();
              // Tenta salvar novamente após a limpeza
              await fileStorageService.saveFile(id, file);
              resolve();
            } catch (cleanupErr) {
              reject(cleanupErr);
            }
          } else {
            reject(error);
          }
        };
      });
    } catch (err) {
      console.error("[Storage] Erro fatal no saveFile:", err);
      throw err;
    }
  },

  deleteFile: async (id: string): Promise<void> => {
    const db = await fileStorageService.init();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  },

  /**
   * Faz upload para o Supabase Storage no bucket 'bookshelf'
   */
  uploadToSupabase: async (userId: string, bookId: string, file: File, customPath?: string): Promise<string> => {
    const extension = file.name.split('.').pop() || 'jpg';
    const filePath = customPath || `${userId}/${bookId}.${extension}`;
    
    const { data, error } = await supabase.storage
      .from('bookshelf')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage
      .from('bookshelf')
      .getPublicUrl(filePath);

    return publicUrl;
  },

  /**
   * Versão genérica para upload de arquivos
   */
  uploadFile: async (userId: string, file: File, bucket = 'bookshelf'): Promise<string> => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    const extension = file.name.split('.').pop() || 'jpg';
    const filePath = `${userId}/uploads/img_${timestamp}_${random}.${extension}`;

    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(filePath);

    return publicUrl;
  },

  /**
   * Faz upload para o Cloudflare R2 via API Worker de forma segura (Auth JWT)
   */
  uploadToR2: async (file: File, folder = 'community-posts-images'): Promise<string> => {
    // 1. Obter a sessão atual para o Token JWT
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session?.access_token) {
      console.error("[Storage] Sessão não encontrada para upload:", sessionError);
      throw new Error("Sessão expirada. Por favor, faça login novamente.");
    }

    const performUpload = async (token: string) => {
      const formData = new FormData();
      formData.append('file', file);
      
      const timestamp = Date.now();
      const random = Math.floor(Math.random() * 1000);
      const extension = file.name.split('.').pop() || 'webp';
      const fileName = `img_${timestamp}_${random}.${extension}`;
      
      formData.append('fileName', fileName);
      formData.append('folder', folder);

      const response = await fetch('/api/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      return response;
    };

    let response = await performUpload(session.access_token);

    // 2. Tentar Refresh uma vez se der 401 (Auth Failure)
    if (response.status === 401) {
      console.warn("[Storage] Token expirado no upload. Tentando renovar...");
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      
      if (!refreshError && refreshData.session?.access_token) {
        response = await performUpload(refreshData.session.access_token);
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Upload to R2 failed with status ${response.status}`);
    }

    const data = await response.json();
    return data.url;
  },

  getFile: async (id: string): Promise<Blob | null> => {
    // Se for uma URL do Supabase (http), buscamos via fetch
    if (id.startsWith('http')) {
      try {
        const response = await fetch(id);
        if (!response.ok) throw new Error('Failed to fetch file from Supabase');
        return await response.blob();
      } catch (err) {
        console.error("Erro ao baixar PDF do Supabase:", err);
        return null;
      }
    }

    // Caso contrário, busca no IndexedDB local
    const db = await fileStorageService.init();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }
};
